// ═══════════════════════════════════════════════════════════════════════════
// curation.js — client wrapper for the Staff Picks (curator_picks) admin RPCs
// (migration 070). Every mutation is gated server-side by can_curate (admin OR
// a manager with the 'content' scope), so these just surface friendly errors.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabase';
import { logAdminAction } from './audit';

function unwrap(data, error) {
  if (error) throw new Error(error.message || 'Request failed');
  if (data?.error) throw new Error(data.error);
  return data;
}

/** All Staff Picks (incl. inactive) with episode title + creator, position-ordered. */
export async function listCuratorPicks() {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('admin_list_curator_picks');
  return unwrap(data, error)?.picks || [];
}

export async function addCuratorPick(episodeId, note = '') {
  const { data, error } = await supabase.rpc('admin_add_curator_pick', {
    p_episode_id: episodeId,
    p_note: note || '',
  });
  const out = unwrap(data, error);
  logAdminAction('feature_episode', { targetType: 'episode', targetId: episodeId });
  return out;
}

export async function removeCuratorPick(id) {
  const { data, error } = await supabase.rpc('admin_remove_curator_pick', { p_id: id });
  const out = unwrap(data, error);
  logAdminAction('unfeature_episode', { targetType: 'curator_pick', targetId: id });
  return out;
}

export async function updateCuratorPick(id, patch = {}) {
  const { data, error } = await supabase.rpc('admin_update_curator_pick', {
    p_id: id,
    p_note: patch.note ?? null,
    p_position: Number.isFinite(patch.position) ? patch.position : null,
    p_is_active: typeof patch.is_active === 'boolean' ? patch.is_active : null,
  });
  const out = unwrap(data, error);
  logAdminAction('update_feature', { targetType: 'curator_pick', targetId: id, detail: patch });
  return out;
}

/** Public episodes an admin can feature — searched by topic. Curated episodes
 *  must be public so every viewer can read them in the home collections. */
export async function searchFeaturableEpisodes(query = '', limit = 15) {
  if (!supabase) return [];
  try {
    let q = supabase
      .from('episodes')
      .select('id, topic, result, user_id, created_at')
      .eq('public', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    const term = (query || '').trim();
    if (term) q = q.ilike('topic', `%${term}%`);
    const { data } = await q;
    return data || [];
  } catch { return []; }
}
